/**
 * Centre de notifications — alertes cliniques + sécurité + IoMT temps réel.
 * v7.2 : branché sur /api/alerts et /api/soc/events-feed.
 */
import { createFileRoute, Link } from "@tanstack/react-router";
import { useEffect, useState } from "react";
import { Card, CardContent } from "@/components/ui/card";
import { Badge } from "@/components/ui/badge";
import { Button } from "@/components/ui/button";
import { ArrowLeft, Bell, AlertTriangle, AlertCircle, Info, Loader2 } from "lucide-react";
import { api } from "@/lib/api";
import { DetailDrawer, type DrawerField } from "@/components/DetailDrawer";

export const Route = createFileRoute("/admin/siem/notifications/")({
  component: Page,
});

interface Alert {
  id: number;
  severity: string;
  source: string;
  title: string;
  body: string | null;
  acknowledged: boolean;
  created_at: string;
}

function Page() {
  const [data, setData] = useState<Alert[]>([]);
  const [stats, setStats] = useState<{ critical: number; warning: number; info: number } | null>(null);
  const [loading, setLoading] = useState(true);
  const [drawer, setDrawer] = useState<Alert | null>(null);
  const [filter, setFilter] = useState<"all" | "active" | "critical">("all");
  const [autoRefresh, setAutoRefresh] = useState(true);

  const reload = async () => {
    try {
      const [r, s] = await Promise.all([api.alerts({ limit: 100 }), api.alertsStats()]);
      setData((r as any).items);
      setStats(s as any);
    } finally { setLoading(false); }
  };

  useEffect(() => {
    reload();
    if (!autoRefresh) return;
    const id = setInterval(reload, 10000);
    return () => clearInterval(id);
  }, [autoRefresh]);

  const filtered = data.filter((a) => {
    if (filter === "active") return !a.acknowledged;
    if (filter === "critical") return a.severity === "critical" && !a.acknowledged;
    return true;
  });

  const acknowledge = async (id: number) => {
    try {
      await api.ackAlert(id);
      setDrawer(null);
      reload();
    } catch (e) { console.error(e); }
  };

  const sevIcon = (s: string) => {
    if (s === "critical") return <AlertCircle className="h-5 w-5 text-red-400" />;
    if (s === "warning") return <AlertTriangle className="h-5 w-5 text-amber-400" />;
    return <Info className="h-5 w-5 text-blue-400" />;
  };

  return (
    <div className="space-y-4 p-6">
      <Link to="/admin/siem" className="inline-flex items-center gap-1 text-sm text-muted-foreground hover:text-foreground">
        <ArrowLeft className="h-3 w-3" /> Dashboard SIEM
      </Link>
      <div>
        <h1 className="text-2xl font-bold tracking-tight flex items-center gap-2">
          <Bell className="h-6 w-6" /> Centre de notifications
          {autoRefresh && <Badge variant="default" className="ml-2 gap-1"><span className="h-2 w-2 rounded-full bg-emerald-400 animate-pulse" />LIVE</Badge>}
        </h1>
        <p className="mt-1 text-sm text-muted-foreground">Alertes cliniques · Sécurité · IoMT · Audit · données live</p>
      </div>

      {stats && (
        <div className="grid grid-cols-2 gap-3 md:grid-cols-5">
          <Card><CardContent className="p-4"><div className="text-xs uppercase text-muted-foreground">Critiques</div><div className="mt-1 text-2xl font-bold text-red-400">{stats.critical}</div></CardContent></Card>
          <Card><CardContent className="p-4"><div className="text-xs uppercase text-muted-foreground">Warnings</div><div className="mt-1 text-2xl font-bold text-amber-400">{stats.warning}</div></CardContent></Card>
          <Card><CardContent className="p-4"><div className="text-xs uppercase text-muted-foreground">Info</div><div className="mt-1 text-2xl font-bold text-blue-400">{stats.info}</div></CardContent></Card>
          <Card><CardContent className="p-4"><div className="text-xs uppercase text-muted-foreground">Total actives</div><div className="mt-1 text-2xl font-bold">{stats.critical + stats.warning + stats.info}</div></CardContent></Card>
          <Card>
            <CardContent className="p-4">
              <div className="text-xs uppercase text-muted-foreground">Auto-refresh</div>
              <Button variant={autoRefresh ? "default" : "outline"} size="sm" className="mt-2" onClick={() => setAutoRefresh(!autoRefresh)}>
                {autoRefresh ? "ON (10s)" : "OFF"}
              </Button>
            </CardContent>
          </Card>
        </div>
      )}

      <div className="flex items-center gap-2">
        <Button variant={filter === "all" ? "default" : "outline"} size="sm" onClick={() => setFilter("all")}>Toutes ({data.length})</Button>
        <Button variant={filter === "active" ? "default" : "outline"} size="sm" onClick={() => setFilter("active")}>Actives ({data.filter((a) => !a.acknowledged).length})</Button>
        <Button variant={filter === "critical" ? "default" : "outline"} size="sm" onClick={() => setFilter("critical")}>Critiques ({data.filter((a) => a.severity === "critical" && !a.acknowledged).length})</Button>
      </div>

      {loading ? (
        <div className="flex items-center justify-center py-12 text-muted-foreground"><Loader2 className="h-5 w-5 animate-spin mr-2" />Chargement…</div>
      ) : (
        <div className="space-y-2">
          {filtered.map((a) => (
            <Card
              key={a.id}
              className={`cursor-pointer hover:border-primary/40 transition ${!a.acknowledged && a.severity === "critical" ? "border-red-500/30" : ""}`}
              onClick={() => setDrawer(a)}
            >
              <CardContent className="p-4">
                <div className="flex items-start gap-3">
                  {sevIcon(a.severity)}
                  <div className="flex-1 min-w-0">
                    <div className="flex items-center gap-2 flex-wrap">
                      <h3 className="font-medium">{a.title}</h3>
                      <Badge variant="outline" className="text-[10px]">{a.source}</Badge>
                      {a.acknowledged && <Badge variant="secondary" className="text-[10px]">✓ Acquittée</Badge>}
                    </div>
                    {a.body && <p className="text-sm text-muted-foreground mt-1">{a.body}</p>}
                    <div className="text-[10px] text-muted-foreground mt-1">{new Date(a.created_at).toLocaleString("fr-FR")}</div>
                  </div>
                </div>
              </CardContent>
            </Card>
          ))}
          {filtered.length === 0 && (
            <div className="py-12 text-center text-muted-foreground">Aucune alerte</div>
          )}
        </div>
      )}

      <DetailDrawer
        open={!!drawer}
        onOpenChange={(o) => !o && setDrawer(null)}
        title={drawer?.title || ""}
        subtitle={drawer?.body || ""}
        badge={drawer ? { label: drawer.severity.toUpperCase(), tone: drawer.severity === "critical" ? "destructive" : drawer.severity === "warning" ? "secondary" : "outline" } : undefined}
        fields={drawer ? [
          { label: "ID", value: String(drawer.id), mono: true },
          { label: "Sévérité", value: drawer.severity },
          { label: "Source", value: drawer.source },
          { label: "Titre", value: drawer.title },
          { label: "Détail", value: drawer.body || "—" },
          { label: "Reçue le", value: new Date(drawer.created_at).toLocaleString("fr-FR") },
          { label: "État", value: drawer.acknowledged ? "Acquittée" : "Active", tone: drawer.acknowledged ? "secondary" : "destructive" },
        ] : []}
        actions={drawer && !drawer.acknowledged ? (
          <Button onClick={() => acknowledge(drawer.id)}><Bell className="h-4 w-4 mr-2" />Acquitter</Button>
        ) : null}
      />
    </div>
  );
}